import { useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo } from "react";
import styled from "styled-components";
import useElementSize from "../hooks/useElementSize";
import { useDeviceType } from "../hooks/useMediaQuery";
import { useProfile } from "../hooks/useTypeProfile";

/* ==============================
   STYLED COMPONENTS
============================== */
const AlertMessage = styled.div`
  strong {
    font-size: 13px;
    margin: 0;
  }
  p {
    font-size: 12px;
    margin: 0 0 10px 0;
  }
`;

const ChatContainer = styled.div`
  height: ${({ heightByType }) => `calc(100vh - ${heightByType})`};
  display: flex;
  flex-direction: column;
`;

const MessagesWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
`;

const MessageBubble = styled.div`
  position: relative;
  text-align: left;
  width: 100%;
  margin-bottom: 10px;

  .bubble {
    font-size: 0.9em;
    width: max-content;
    max-width: 100%;
    margin-bottom: 3px;
    background-color: ${({ bg }) => bg};
    border: 1px solid ${({ border }) => border};
    color: ${({ color }) => color};
    padding: 8px 15px;
    border-radius: 20px;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
    margin-left: ${({ align }) => (align === "right" ? "auto" : 0)};
    margin-right: ${({ align }) => (align === "right" ? 0 : "auto")};
    align-self: ${({ align }) => (align === "right" ? "flex-start" : "flex-end")};
  }

  .meta {
    font-size: 0.7em;
    text-align: ${({ align }) => align};
    color: #444;
    position: relative;
    margin-bottom: 10px;
    font-weight: 400;
  }
`;

const InputContainer = styled.div`
  padding: 10px 0;
  border-top: 1px solid #eee;
  display: flex;
  width: 100%;
  background: white;
  position: relative;
`;

const Editor = styled.div`
  flex: 1;
  text-align: left;
  padding: 10px 15px;
  border-radius: 20px;
  border: 1px solid #ccc;
  font-size: 1em;
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-y: auto;
  min-height: 40px;
  max-height: 50px;
  max-width: 100%;
`;

const SendButton = styled.button`
  margin-left: 10px;
  width: 50px;
  height: 50px;
  border-radius: 30px;
  border: none;
  background-color: #000;
  color: #fff;
  cursor: pointer;
  display: grid;
  justify-content: center;
  align-items: center;
`;

const SuggestionsList = styled.ul`
  position: absolute;
  bottom: 60px;
  left: 10px;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 5px;
  list-style: none;
  padding: 5px 0;
  margin: 0;
  z-index: 10;
  width: 200px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const SuggestionItem = styled.li`
  padding: 8px 12px;
  background-color: ${({ active }) => (active ? "#eee" : "#fff")};
  cursor: pointer;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  text-align: center;
  padding: 2rem 1rem;

  svg {
    width: 60px;
    height: 60px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 0.9rem;
    color: #666;
    margin: 0;
  }
`;

/* ==============================
   COMPONENTE PRINCIPAL
============================== */

const ChatViewer = forwardRef(
    (
        {
            messages = [],
            onSend = () => { },
            onInputChange,
            users = [],
            heightTab = 0,
            isCEO,
            type: forcedType,
            currentUser: forcedCurrentUser,
            onMark,
        },
        ref
    ) => {
        const { isDesktop } = useDeviceType()
        const { getTypeRole, getProfileId } = useProfile();
        const currentUser = forcedCurrentUser || getProfileId();
        const type = forcedType || getTypeRole();
        const bottomRef = useRef(null);
        const editorRef = useRef(null);
        const inputContainer = useRef(null);
        const alertMessageContainer = useRef(null);
        const onMarkRef = useRef(onMark);

        const [editorText, setEditorText] = useState("");
        const [mentionSuggestions, setMentionSuggestions] = useState([]);
        const [showSuggestions, setShowSuggestions] = useState(false);
        const [mentionIndex, setMentionIndex] = useState(0);

        const { height } = useElementSize(inputContainer);
        const { height: h } = useElementSize(alertMessageContainer);
        const canCompose = type === "resto" && !isCEO;
        const showRestoInstructions = type === "resto";

        useEffect(() => {
            onMarkRef.current = onMark;
        }, [onMark]);

        const messageSyncKey = useMemo(
            () =>
                (messages || [])
                    .map((msg) => `${msg._id}:${msg.isRead}:${(msg.readBy || []).length}`)
                    .join("|"),
            [messages]
        );

        useEffect(() => {
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
            if (onMarkRef.current) onMarkRef.current();
        }, [messageSyncKey]);

        const heightByType = canCompose
            ? isDesktop
                ? "350px"
                : "365px"
            : isDesktop
                ? "300px"
                : "295px";

        const handleSend = () => {
            if (!editorRef.current) return;
            const html = editorRef.current.innerHTML.trim();
            const text = editorRef.current.innerText.trim();

            if (!text) return;

            const mentionElements = editorRef.current.querySelectorAll("[data-mention]");
            const mentionedLabels = Array.from(
                new Set(
                    Array.from(mentionElements)
                        .map((el) => el.getAttribute("data-mention"))
                        .filter(Boolean)
                )
            );

            const mentionedUsers = Array.from(
                new Map(
                    users
                        .filter((u) => mentionedLabels.includes(u.label))
                        .map((u) => [String(u.value), { label: u.label, value: u.value }])
                ).values()
            );

            const hasAll = mentionedLabels.some(m => m.toLowerCase() === "todos");
            const payload = {
                message: text,
                html,
                mentions: hasAll
                    ? [{ label: "todos", value: "ALL_USERS" }]
                    : mentionedUsers,
            };

            console.log("[ChatViewer] handleSend payload", {
                type,
                text,
                mentionedLabels,
                mentionedUsers,
                hasAll,
                usersCount: users.length,
            });

            onSend(payload);

            editorRef.current.innerHTML = "";
            setEditorText("");
            setShowSuggestions(false);
        };


        const renderContent = (text = "") => {
            return text.split(/(@[\wÀ-ÿ]+)/g).map((part, i) =>
                part.startsWith("@") ? (
                    <span key={i} style={{ color: "#ff0102", fontWeight: "bold" }}>
                        {part}
                    </span>
                ) : (
                    <span key={i}>{part}</span>
                )
            );
        };

        const insertMention = (username) => {
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;
            const range = selection.getRangeAt(0);
            const textNode = range.startContainer;
            const offset = range.startOffset;

            const text = textNode.textContent;
            const match = text.slice(0, offset).match(/@([\wÀ-ÿ]*)$/);
            if (!match) return;

            const start = match.index;
            const before = text.slice(0, start);
            const after = text.slice(offset);

            const mentionSpan = document.createElement("span");
            mentionSpan.textContent = `@${username}`;
            mentionSpan.setAttribute("data-mention", username);
            mentionSpan.contentEditable = "false";
            mentionSpan.style.color = "#ff0102";
            mentionSpan.style.fontWeight = "bold";
            mentionSpan.style.backgroundColor = "#f5f5f5";
            mentionSpan.style.borderRadius = "4px";
            mentionSpan.style.padding = "2px 4px";
            mentionSpan.style.marginRight = "2px";
            mentionSpan.style.display = "inline-block";

            const space = document.createTextNode("\u00A0");
            const afterText = document.createTextNode(after);

            const parent = textNode.parentNode;
            const beforeNode = document.createTextNode(before);
            parent.replaceChild(beforeNode, textNode);

            const frag = document.createDocumentFragment();
            frag.appendChild(beforeNode);
            frag.appendChild(mentionSpan);
            frag.appendChild(space);
            frag.appendChild(afterText);
            parent.appendChild(frag);

            const newRange = document.createRange();
            newRange.setStartAfter(space);
            newRange.collapse(true);
            const selection2 = window.getSelection();
            selection2.removeAllRanges();
            selection2.addRange(newRange);

            editorRef.current.focus();
            handleInput();
            setShowSuggestions(false);
        };

        const insertMentionAtEnd = (username) => {
            if (!editorRef.current) return;
            const editor = editorRef.current;
            const mentionSpan = document.createElement("span");
            mentionSpan.textContent = `@${username}`;
            mentionSpan.setAttribute("data-mention", username);
            mentionSpan.contentEditable = "false";
            mentionSpan.style.color = "#ff0102";
            mentionSpan.style.fontWeight = "bold";
            mentionSpan.style.backgroundColor = "#f5f5f5";
            mentionSpan.style.borderRadius = "4px";
            mentionSpan.style.padding = "2px 4px";
            mentionSpan.style.marginRight = "2px";
            mentionSpan.style.display = "inline-block";

            const space = document.createTextNode("\u00A0");
            editor.appendChild(mentionSpan);
            editor.appendChild(space);

            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(editor);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);

            editor.focus();
            handleInput();
            setShowSuggestions(false);
        };

        useImperativeHandle(ref, () => ({
            insertMentionExtern: (username) => insertMentionAtEnd(username),
        }));

        const getCaretText = () => {
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return "";
            const range = selection.getRangeAt(0);
            const precedingText =
                range.startContainer.textContent?.slice(0, range.startOffset) || "";
            const match = precedingText.match(/@([\wÀ-ÿ]*)$/);
            return match ? match[1] : "";
        };

        const handleInput = () => {
            const text = editorRef.current.innerText;
            setEditorText(text);

            const word = getCaretText();
            if (word && word.length > 0) {
                const filtered = users.filter((u) =>
                    u.label.toLowerCase().startsWith(word.toLowerCase())
                );
                if ("todos".startsWith(word.toLowerCase())) {
                    filtered.unshift({ label: "todos", value: "ALL_USERS" });
                }
                setMentionSuggestions(filtered);
                setShowSuggestions(filtered.length > 0);
                setMentionIndex(0);
            } else {
                setShowSuggestions(false);
                setMentionSuggestions([]);
                setMentionIndex(-1);
            }
        };

        return (
            <>
                {showRestoInstructions && (
                    <AlertMessage ref={alertMessageContainer}>
                        <strong>Motiva a tu equipo directamente:</strong>
                        <p>
                            Usa @nombre para enviar un mensaje directo o @todos para hablarle a
                            todo el equipo.
                        </p>
                    </AlertMessage>
                )}

                <ChatContainer heightByType={heightByType}>
                    <MessagesWrapper>
                        {messages.length === 0 ? (
                            <EmptyState>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8 10h8M8 14h5m-9 4h14a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                </svg>
                                <p>Aún no hay mensajes. {type === "resto" ? "¡Envía el primero!" : null}</p>
                            </EmptyState>
                        ) : (
                            [...messages].reverse().map((msg, index) => {
                                const align = msg.senderId === currentUser ? "right" : "left";
                                const bg = type === "resto" ? "#000" : "#fff";
                                const border = type === "resto" ? "#000" : "#FF0102";
                                const color = type === "resto" ? "#FFF" : "#FF0102";
                                const messageContent = msg.contentRaw || msg.content || "";
                                return (
                                    <MessageBubble
                                        key={index}
                                        align={align}
                                        bg={bg}
                                        border={border}
                                        color={color}
                                    >
                                        <div className="bubble">{renderContent(messageContent)}</div>
                                        <div className="meta">
                                            <span>{msg.time}</span> —{" "}
                                            {type === "resto"
                                                ? msg.type === "SINGLE"
                                                    ? `Enviado a ${msg.mentionedUserIds
                                                        ?.map((e) => `@${e.name}`)
                                                        .join(" ")}`
                                                    : "@Todos"
                                                : msg.type === "SINGLE"
                                                    ? `@${msg.user || msg.resto || ""}`
                                                    : "@Todos"}
                                        </div>
                                    </MessageBubble>
                                );
                            })
                        )}
                    </MessagesWrapper>
                </ChatContainer>

                {canCompose && (
                    <InputContainer ref={inputContainer}>
                        <Editor
                            ref={editorRef}
                            contentEditable
                            onInput={handleInput}
                            onKeyDown={(e) => {
                                // flechas para navegar sugerencias
                                if (showSuggestions && mentionSuggestions.length > 0) {
                                    if (e.key === "ArrowDown") {
                                        e.preventDefault();
                                        setMentionIndex((prev) => (prev + 1) % mentionSuggestions.length);
                                    } else if (e.key === "ArrowUp") {
                                        e.preventDefault();
                                        setMentionIndex(
                                            (prev) => (prev - 1 + mentionSuggestions.length) % mentionSuggestions.length
                                        );
                                    } else if (e.key === "Enter") {
                                        e.preventDefault();
                                        insertMention(mentionSuggestions[mentionIndex].label);
                                    }
                                }
                            }}
                            placeholder="Escribe un mensaje..."
                        />
                        <SendButton onClick={handleSend}>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                                    stroke="#FFFFFF"
                                    strokeWidth="1.5"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M10.74 15.53L14.26 12L10.74 8.46997"
                                    stroke="#FFFFFF"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </SendButton>

                        {showSuggestions && mentionSuggestions.length > 0 && (
                            <SuggestionsList>
                                {mentionSuggestions.map((user, index) => (
                                    <SuggestionItem
                                        key={user.value}
                                        active={mentionIndex === index}
                                        onClick={() => insertMention(user.label)}
                                        onTouchStart={() => insertMention(user.label)}
                                    >
                                        @{user.label}
                                    </SuggestionItem>
                                ))}
                            </SuggestionsList>
                        )}
                    </InputContainer>
                )}
            </>
        );
    }
);

export default ChatViewer;
